/**
 * Achievement Showcase Component
 * Combines achievement badges, timeline, and interactive displays
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AnimatedContainer } from '../lib/animations';
import { AchievementGrid, AchievementDetail, achievementPresets, type Achievement } from './AchievementDisplay';
import { AnimatedTimeline, timelinePresets, type TimelineItem } from './AnimatedTimeline';
import { 
  Award, Trophy, Star, Medal, Shield, CheckCircle, 
  Calendar, Building, Users, MapPin, Target, Zap,
  Filter, Grid, Clock, BarChart3, Eye
} from 'lucide-react';

// View modes for the showcase
type ViewMode = 'badges' | 'timeline' | 'stats' | 'combined';

// Filter options
interface FilterOptions {
  type?: string;
  year?: number;
  level?: string;
  category?: string;
}

interface AchievementShowcaseProps {
  achievements: Achievement[];
  timelineItems: TimelineItem[];
  title?: string;
  subtitle?: string;
  className?: string;
  defaultView?: ViewMode;
  showFilters?: boolean;
  showViewToggle?: boolean;
  triggerOnScroll?: boolean;
  threshold?: number;
}

// Sample data for demonstration
const sampleAchievements: Achievement[] = [
  {
    id: 'hia-award-2023',
    title: 'HIA Home of the Year',
    description: 'Awarded for outstanding design and construction excellence in residential building.',
    type: 'award',
    year: 2023,
    organization: 'Housing Industry Association',
    location: 'Auckland, New Zealand',
    icon: Trophy,
    color: '#f59e0b',
    level: 'gold',
    verified: true,
    details: [
      'Recognized for innovative design and sustainable building practices',
      'Judged by industry experts and architects',
      'Competed against 200+ entries nationwide'
    ],
    stats: [
      { label: 'Entries', value: '200+' },
      { label: 'Categories', value: '12' },
      { label: 'Judges', value: '15' }
    ],
    tags: ['Design Excellence', 'Sustainability', 'Innovation']
  },
  {
    id: 'master-builder-2022',
    title: 'Master Builder Certification',
    description: 'Certified Master Builder status recognizing superior craftsmanship and business practices.',
    type: 'certification',
    year: 2022,
    organization: 'Master Builders Association',
    icon: Shield,
    color: '#10b981',
    level: 'platinum',
    verified: true,
    details: [
      'Rigorous assessment of building quality and business practices',
      'Ongoing professional development requirements',
      'Customer satisfaction guarantee'
    ],
    tags: ['Certification', 'Quality Assurance', 'Professional Development']
  },
  {
    id: 'eco-builder-2023',
    title: 'Eco-Friendly Builder Award',
    description: 'Recognition for commitment to sustainable building practices and environmental responsibility.',
    type: 'recognition',
    year: 2023,
    organization: 'Green Building Council',
    icon: Star,
    color: '#22c55e',
    level: 'gold',
    verified: true,
    details: [
      'Demonstrated use of sustainable materials and practices',
      'Energy-efficient building designs',
      'Waste reduction and recycling programs'
    ],
    stats: [
      { label: 'CO2 Reduced', value: '15 tons' },
      { label: 'Waste Diverted', value: '85%' },
      { label: 'Energy Saved', value: '30%' }
    ],
    tags: ['Sustainability', 'Environment', 'Green Building']
  },
  {
    id: 'safety-excellence-2023',
    title: 'Safety Excellence Award',
    description: 'Outstanding safety record and commitment to workplace safety standards.',
    type: 'award',
    year: 2023,
    organization: 'Construction Safety Council',
    icon: Shield,
    color: '#ef4444',
    level: 'gold',
    verified: true,
    details: [
      'Zero workplace accidents for 24 consecutive months',
      'Comprehensive safety training programs',
      'Regular safety audits and improvements'
    ],
    stats: [
      { label: 'Accident-Free Days', value: '730+' },
      { label: 'Safety Training Hours', value: '500+' },
      { label: 'Safety Score', value: '98%' }
    ],
    tags: ['Safety', 'Training', 'Excellence']
  },
  {
    id: 'customer-choice-2022',
    title: 'Customer Choice Award',
    description: 'Voted by customers as the preferred builder for quality and service.',
    type: 'recognition',
    year: 2022,
    organization: 'Consumer Choice Awards',
    icon: Star,
    color: '#8b5cf6',
    level: 'gold',
    verified: true,
    details: [
      'Based on customer satisfaction surveys',
      '95% customer satisfaction rating',
      'Excellent communication and service delivery'
    ],
    stats: [
      { label: 'Satisfaction', value: '95%' },
      { label: 'Customers Surveyed', value: '150+' },
      { label: 'Repeat Customers', value: '40%' }
    ],
    tags: ['Customer Service', 'Satisfaction', 'Quality']
  },
  {
    id: 'innovation-award-2021',
    title: 'Building Innovation Award',
    description: 'Recognition for innovative building techniques and technology adoption.',
    type: 'award',
    year: 2021,
    organization: 'Building Innovation Institute',
    icon: Zap,
    color: '#06b6d4',
    level: 'silver',
    verified: true,
    details: [
      'Pioneered use of 3D modeling and virtual reality',
      'Implemented smart home technology integration',
      'Developed efficient construction methodologies'
    ],
    tags: ['Innovation', 'Technology', 'Efficiency']
  }
];

const sampleTimelineItems: TimelineItem[] = [
  {
    id: 'company-founded',
    title: 'Company Founded',
    description: 'Rolleston Homes was established with a vision to build quality homes for New Zealand families.',
    date: 'January 2009',
    year: 2009,
    category: 'Milestone',
    location: 'Rolleston, Canterbury',
    icon: Building,
    color: '#3b82f6',
    importance: 'critical',
    details: [
      'Started with a small team of 3 people',
      'First office established in Rolleston',
      'Initial focus on custom home building'
    ],
    stats: [
      { label: 'Initial Team', value: '3' },
      { label: 'First Year Homes', value: '5' }
    ],
    tags: ['Founding', 'Milestone', 'Beginning']
  },
  {
    id: 'first-award',
    title: 'First Industry Award',
    description: 'Received our first recognition from the local building association for construction quality.',
    date: 'March 2011',
    year: 2011,
    category: 'Achievement',
    location: 'Canterbury',
    icon: Award,
    color: '#f59e0b',
    importance: 'high',
    details: [
      'Canterbury Building Association Excellence Award',
      'Recognized for attention to detail and craftsmanship',
      'First of many awards to come'
    ],
    tags: ['Award', 'Recognition', 'Quality']
  },
  {
    id: 'team-expansion',
    title: 'Major Team Expansion',
    description: 'Expanded our team to 15 professionals to meet growing demand.',
    date: 'June 2015',
    year: 2015,
    category: 'Growth',
    location: 'Canterbury',
    icon: Users,
    color: '#10b981',
    importance: 'high',
    details: [
      'Hired experienced builders and project managers',
      'Established dedicated design team',
      'Improved project delivery capabilities'
    ],
    stats: [
      { label: 'Team Size', value: '15' },
      { label: 'Annual Capacity', value: '25 homes' }
    ],
    tags: ['Growth', 'Team', 'Expansion']
  },
  {
    id: 'sustainability-focus',
    title: 'Sustainability Initiative',
    description: 'Launched comprehensive sustainability program focusing on eco-friendly building practices.',
    date: 'September 2018',
    year: 2018,
    category: 'Innovation',
    location: 'Canterbury',
    icon: Star,
    color: '#22c55e',
    importance: 'high',
    details: [
      'Implemented green building standards',
      'Partnered with sustainable material suppliers',
      'Achieved first Green Building certification'
    ],
    stats: [
      { label: 'CO2 Reduction', value: '25%' },
      { label: 'Waste Reduction', value: '40%' }
    ],
    tags: ['Sustainability', 'Environment', 'Innovation']
  },
  {
    id: 'digital-transformation',
    title: 'Digital Transformation',
    description: 'Adopted cutting-edge technology including 3D modeling and virtual reality for client presentations.',
    date: 'February 2020',
    year: 2020,
    category: 'Technology',
    location: 'Canterbury',
    icon: Zap,
    color: '#8b5cf6',
    importance: 'medium',
    details: [
      'Implemented 3D design software',
      'Virtual reality client presentations',
      'Digital project management systems'
    ],
    tags: ['Technology', 'Innovation', 'Digital']
  },
  {
    id: 'milestone-200',
    title: '200th Home Completed',
    description: 'Celebrated the completion of our 200th home, marking a significant milestone in our journey.',
    date: 'August 2022',
    year: 2022,
    category: 'Milestone',
    location: 'Canterbury',
    icon: Trophy,
    color: '#f59e0b',
    importance: 'critical',
    details: [
      'Milestone celebration with team and clients',
      'Featured in local media',
      'Reflection on 13 years of growth'
    ],
    stats: [
      { label: 'Homes Built', value: '200' },
      { label: 'Happy Families', value: '200' },
      { label: 'Years in Business', value: '13' }
    ],
    tags: ['Milestone', 'Achievement', 'Celebration']
  }
];

export const AchievementShowcase: React.FC<AchievementShowcaseProps> = ({
  achievements = sampleAchievements,
  timelineItems = sampleTimelineItems,
  title = 'Our Achievements',
  subtitle = 'A showcase of our awards, certifications, and company milestones',
  className = '',
  defaultView = 'combined',
  showFilters = true,
  showViewToggle = true,
  triggerOnScroll = true,
  threshold = 0.1
}) => {
  const [currentView, setCurrentView] = useState<ViewMode>(defaultView);
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const [filters, setFilters] = useState<FilterOptions>({});
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Filter achievements based on current filters
  const filteredAchievements = achievements.filter(achievement => {
    if (filters.type && achievement.type !== filters.type) return false;
    if (filters.year && achievement.year !== filters.year) return false;
    if (filters.level && achievement.level !== filters.level) return false;
    return true;
  });

  // Get unique filter values
  const uniqueTypes = [...new Set(achievements.map(a => a.type))];
  const uniqueYears = [...new Set(achievements.map(a => a.year))].sort((a, b) => b - a);
  const uniqueLevels = [...new Set(achievements.map(a => a.level).filter(Boolean))];

  // View toggle options
  const viewOptions = [
    { key: 'badges', label: 'Badges', icon: Grid },
    { key: 'timeline', label: 'Timeline', icon: Clock },
    { key: 'stats', label: 'Statistics', icon: BarChart3 },
    { key: 'combined', label: 'Combined', icon: Eye }
  ];

  const handleAchievementClick = (achievement: Achievement) => {
    setSelectedAchievement(achievement);
    setIsDetailOpen(true);
  };

  const handleFilterChange = (filterType: keyof FilterOptions, value: any) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType] === value ? undefined : value
    }));
  };

  return (
    <div className={`w-full ${className}`}>
      <AnimatedContainer triggerOnScroll={triggerOnScroll} threshold={threshold}>
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {subtitle}
          </p>
        </motion.div>

        {/* Controls */}
        <motion.div
          className="flex flex-col lg:flex-row items-center justify-between mb-8 space-y-4 lg:space-y-0"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          {/* View Toggle */}
          {showViewToggle && (
            <div className="flex bg-gray-100 rounded-lg p-1">
              {viewOptions.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setCurrentView(key as ViewMode)}
                  className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    currentView === key
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon size={16} className="mr-2" />
                  {label}
                </button>
              ))}
            </div>
          )}

          {/* Filters */}
          {showFilters && currentView !== 'timeline' && (
            <div className="flex items-center space-x-4">
              <Filter size={16} className="text-gray-500" />
              
              {/* Type Filter */}
              <select
                value={filters.type || ''}
                onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                {uniqueTypes.map(type => (
                  <option key={type} value={type}>
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </option>
                ))}
              </select>

              {/* Year Filter */}
              <select
                value={filters.year || ''}
                onChange={(e) => handleFilterChange('year', e.target.value ? parseInt(e.target.value) : undefined)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Years</option>
                {uniqueYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>

              {/* Level Filter */}
              <select
                value={filters.level || ''}
                onChange={(e) => handleFilterChange('level', e.target.value || undefined)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Levels</option>
                {uniqueLevels.map(level => (
                  <option key={level} value={level}>
                    {level?.charAt(0).toUpperCase() + level?.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </motion.div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentView}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            {currentView === 'badges' && (
              <AchievementGrid
                achievements={filteredAchievements}
                config={achievementPresets.awards}
                onAchievementClick={handleAchievementClick}
                triggerOnScroll={false}
              />
            )}

            {currentView === 'timeline' && (
              <AnimatedTimeline
                items={timelineItems}
                config={timelinePresets.companyHistory}
                triggerOnScroll={false}
              />
            )}

            {currentView === 'stats' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <motion.div
                  className="bg-white p-6 rounded-xl shadow-lg text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {achievements.length}
                  </div>
                  <div className="text-gray-600">Total Awards</div>
                </motion.div>

                <motion.div
                  className="bg-white p-6 rounded-xl shadow-lg text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {achievements.filter(a => a.level === 'gold').length}
                  </div>
                  <div className="text-gray-600">Gold Awards</div>
                </motion.div>

                <motion.div
                  className="bg-white p-6 rounded-xl shadow-lg text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <div className="text-3xl font-bold text-purple-600 mb-2">
                    {achievements.filter(a => a.verified).length}
                  </div>
                  <div className="text-gray-600">Verified</div>
                </motion.div>

                <motion.div
                  className="bg-white p-6 rounded-xl shadow-lg text-center"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="text-3xl font-bold text-orange-600 mb-2">
                    {new Date().getFullYear() - Math.min(...achievements.map(a => a.year))}
                  </div>
                  <div className="text-gray-600">Years of Excellence</div>
                </motion.div>
              </div>
            )}

            {currentView === 'combined' && (
              <div className="space-y-16">
                {/* Recent Achievements */}
                <section>
                  <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                    Recent Achievements
                  </h3>
                  <AchievementGrid
                    achievements={filteredAchievements.slice(0, 6)}
                    config={achievementPresets.awards}
                    onAchievementClick={handleAchievementClick}
                    triggerOnScroll={false}
                  />
                </section>

                {/* Company Timeline */}
                <section>
                  <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                    Our Journey
                  </h3>
                  <AnimatedTimeline
                    items={timelineItems.slice(0, 4)}
                    config={timelinePresets.companyHistory}
                    triggerOnScroll={false}
                  />
                </section>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Achievement Detail Modal */}
        <AchievementDetail
          achievement={selectedAchievement}
          isOpen={isDetailOpen}
          onClose={() => {
            setIsDetailOpen(false);
            setSelectedAchievement(null);
          }}
        />
      </AnimatedContainer>
    </div>
  );
};

export default AchievementShowcase;